import { createFileRoute, linkOptions, Navigate, redirect } from '@tanstack/react-router';
import { useUserProfileContext } from '@entities/user-profile';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { HelpdeskList, helpdeskSearchSchema } from '@features/helpdesk';
import { CrmCustomerHelpdeskCreateForm } from '@pages/crm';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessHelpdesk } from '@shared/lib';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/helpdesk'
)({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessHelpdesk(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: CustomerHelpdeskRoute,
});

function CustomerHelpdeskRoute() {
  const { userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const profileContext = useUserProfileContext(user?.id ?? '');
  const getRequestLink = (taskUuid: string) =>
    linkOptions({
      to: '/$locale/crm/helpdesk/$taskUuid',
      params: { locale, taskUuid },
      search: { ...helpdeskSearchSchema.parse(search), helpdeskCustomerId: customerId },
    });
  if (!canAccessHelpdesk(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }
  return (
    <HelpdeskList
      createFormComponent={CrmCustomerHelpdeskCreateForm}
      canCreate={ability.can('create', 'HelpdeskTask')}
      canUpdate={ability.can('update', 'HelpdeskTask')}
      canDelete={ability.can('delete', 'HelpdeskTask')}
      viewerProfileId={profileContext.data?.data.profile_ref}
      customerId={customerId}
      viewerAccessScope={user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : ''}
      search={helpdeskSearchSchema.parse(search)}
      onSearchChange={(next) => {
        void navigate({
          search: (current) => ({ ...current, ...next }),
          replace: true,
          resetScroll: false,
        });
      }}
      getRequestLink={getRequestLink}
      onOpen={(taskUuid) => {
        void navigate(getRequestLink(taskUuid));
      }}
    />
  );
}
